import { Button } from "@dmca/frontend/components/button";
import { Alert, Box, Card, Typography } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/kanban/order-approval/$token")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: "Kanban Order Approval | DNA Dashboard",
      },
    ],
  }),
});

export function getApprovalMessage(
  status?: string,
  action: "create" | "complete" | "cancel" = "create"
) {
  const label =
    action === "complete"
      ? "completion"
      : action === "cancel"
      ? "cancellation"
      : "creation";
  const result =
    action === "complete"
      ? "completed"
      : action === "cancel"
      ? "cancelled"
      : "created";
  switch (status) {
    case "pending":
      return {
        severity: "info" as const,
        title: "Action Required",
        body: `Click the button below to approve this Kanban order ${label}.`,
      };
    case "approved":
      return {
        severity: "success" as const,
        title: "Order Approved",
        body: `The Kanban order has been ${result}.`,
      };
    case "already-completed":
      return {
        severity: "info" as const,
        title: "Already Completed",
        body: "This Kanban order has already been completed.",
      };
    case "already-closed":
      return {
        severity: "info" as const,
        title: "Order No Longer Active",
        body: "This Kanban order has already been completed or cancelled.",
      };
    case "already-approved":
      return {
        severity: "info" as const,
        title: "Already Approved",
        body: "This approval link has already been used.",
      };
    case "already-active":
      return {
        severity: "info" as const,
        title: "Order Already Exists",
        body: "This Kanban already has an active order.",
      };
    case "expired":
      return {
        severity: "warning" as const,
        title: "Approval Link Expired",
        body: "Ask the dashboard user to send a new approval request.",
      };
    case "cancelled":
      return {
        severity: "warning" as const,
        title: "Request Cancelled",
        body: "This request has been cancelled.",
      };
    case "superseded":
      return {
        severity: "info" as const,
        title: "Newer Request Sent",
        body: "This request is out of date. A newer approval request has been sent.",
      };
    case "invalid":
      return {
        severity: "error" as const,
        title: "Invalid Approval Link",
        body: "This approval link is invalid or no longer available.",
      };
    default:
      return {
        severity: "error" as const,
        title: "Approval Failed",
        body: "The order could not be approved. Please try again.",
      };
  }
}

function RouteComponent() {
  const { token } = Route.useParams();
  const { trpcQcProxy: trpc } = Route.useRouteContext();
  const approvalRequest = useQuery(
    trpc.kanban.getOrderApprovalRequestByToken.queryOptions({ token })
  );
  const mutation = useMutation(
    trpc.kanban.approveOrderApprovalRequest.mutationOptions()
  );
  const [now, setNow] = useState(Date.now);

  useEffect(() => {
    if (approvalRequest.data?.status !== "pending") return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => window.clearInterval(timer);
  }, [approvalRequest.data?.status]);

  const secondsRemaining = approvalRequest.data
    ? Math.max(
        0,
        Math.ceil((approvalRequest.data.expiresAt.valueOf() - now) / 1000)
      )
    : 0;
  const isExpired =
    approvalRequest.data?.status === "pending" && secondsRemaining === 0;
  const status =
    mutation.data?.status ??
    (isExpired ? "expired" : approvalRequest.data?.status ?? "invalid");

  const isCompletion = approvalRequest.data?.action === "complete";
  const isCancellation = approvalRequest.data?.action === "cancel";
  const message = getApprovalMessage(status, approvalRequest.data?.action);
  const canApprove = approvalRequest.data?.status === "pending" && !isExpired;
  const expiry = `${String(Math.floor(secondsRemaining / 3600)).padStart(
    2,
    "0"
  )}:${String(Math.floor((secondsRemaining % 3600) / 60)).padStart(
    2,
    "0"
  )}:${String(secondsRemaining % 60).padStart(2, "0")}`;
  const kanbanLabel = approvalRequest.data?.kanbanId
    ? approvalRequest.data.kanbanName
      ? `${approvalRequest.data.kanbanName} (${approvalRequest.data.kanbanId})`
      : approvalRequest.data.kanbanId
    : null;

  return (
    <Box
      sx={{
        display: "grid",
        minHeight: "100dvh",
        p: { xs: 2, sm: 3 },
        placeItems: "center",
        width: "100%",
      }}
    >
      <Card sx={{ maxWidth: 520, p: { xs: 3, sm: 4 }, width: "100%" }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          Kanban Order{" "}
          {isCompletion
            ? "Completion"
            : isCancellation
            ? "Cancellation"
            : "Creation"}{" "}
          Approval
        </Typography>
        {kanbanLabel && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {kanbanLabel}
          </Typography>
        )}
        {approvalRequest.data?.requesterId && (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Requested by: {approvalRequest.data.requesterName ?? "Unknown user"}
            {` (User ID: ${approvalRequest.data.requesterId})`}
          </Typography>
        )}
        {approvalRequest.isPending ? (
          <Typography>Checking approval link...</Typography>
        ) : approvalRequest.isError || mutation.isError ? (
          <Alert severity="error">
            <Typography variant="subtitle1">Approval Failed</Typography>
            <Typography>
              The order could not be approved. Please try again.
            </Typography>
          </Alert>
        ) : (
          <Alert severity={message.severity}>
            <Typography variant="subtitle1">{message.title}</Typography>
            <Typography>{message.body}</Typography>
          </Alert>
        )}
        {canApprove && (
          <>
            <Typography sx={{ mt: 3 }}>Link expires in {expiry}</Typography>
            <Button
              loading={mutation.isPending}
              onClick={() => mutation.mutate({ token })}
              sx={{ mt: 2 }}
            >
              Approve{" "}
              {isCompletion
                ? "Completion"
                : isCancellation
                ? "Cancellation"
                : "Order"}
            </Button>
          </>
        )}
        <Button href="/login" variant="outlined" sx={{ mt: 3 }}>
          Go to Dashboard
        </Button>
      </Card>
    </Box>
  );
}
